// Client for /api — production calls Render directly (VITE_API_BASE in netlify.toml).
// Local dev uses Vite proxy → localhost:5174. Netlify Functions are a fallback only.

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
const RETRYABLE = new Set([502, 503, 504]);
const RETRY_DELAYS_MS = [1000, 2000, 3500];
const USE_ASYNC_JOBS = import.meta.env.PROD && !API_BASE;

export function apiUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${p}`;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function friendlyNetworkError(err) {
  if (err?.message === "Failed to fetch" || err instanceof TypeError) {
    return new Error(
      "Could not reach the Shorts server. If the video just finished rendering, wait for the latest deploy to finish and try again."
    );
  }
  return err;
}

function normalizeApiResourceUrl(url) {
  const value = String(url || "");
  if (!value || value.startsWith("data:") || /^https?:\/\//i.test(value)) return value;
  return value.startsWith("/api/") ? apiUrl(value) : value;
}

function normalizeShortsResult(result) {
  if (!result || typeof result !== "object") return result;
  const variants = Array.isArray(result.variants)
    ? result.variants.map((variant) => ({
        ...variant,
        video: normalizeApiResourceUrl(variant?.video || variant?.videoUrl),
        videoUrl: normalizeApiResourceUrl(variant?.videoUrl || variant?.video),
      }))
    : [];
  const assets = Array.isArray(result.assets)
    ? result.assets.map((asset) => ({
        ...asset,
        image: normalizeApiResourceUrl(asset?.image),
        audio: normalizeApiResourceUrl(asset?.audio),
      }))
    : result.assets;
  return {
    ...result,
    variants,
    assets,
    video: normalizeApiResourceUrl(result.video || variants[0]?.video),
    videoUrl: normalizeApiResourceUrl(result.videoUrl || variants[0]?.videoUrl),
  };
}

function mergeSignals(userSignal, timeoutMs) {
  if (!userSignal && !timeoutMs) return undefined;
  const ctrl = new AbortController();
  const onAbort = () => ctrl.abort();
  if (userSignal) {
    if (userSignal.aborted) {
      ctrl.abort();
      return ctrl.signal;
    }
    userSignal.addEventListener("abort", onAbort, { once: true });
  }
  let timer;
  if (timeoutMs) {
    timer = setTimeout(onAbort, timeoutMs);
    ctrl.signal.addEventListener("abort", () => clearTimeout(timer), {
      once: true,
    });
  }
  return ctrl.signal;
}

function startStatusTicker(onStatus, connecting, processing) {
  if (!onStatus) return () => {};
  onStatus(connecting);
  const timer = setTimeout(() => onStatus(processing), 12000);
  return () => clearTimeout(timer);
}

async function fetchWithRetry(url, options = {}, config = {}) {
  const {
    onStatus,
    retries = RETRY_DELAYS_MS.length,
    timeoutMs,
    connectingMessage = "Contacting NVIDIA NIM…",
    processingMessage = "NVIDIA is processing your request…",
  } = config;
  const signal = mergeSignals(options.signal, timeoutMs);
  let lastRes;
  let stopTicker = () => {};

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    stopTicker();
    if (attempt === 0) {
      stopTicker = startStatusTicker(
        onStatus,
        connectingMessage,
        processingMessage
      );
    } else {
      onStatus?.(`Retrying NVIDIA request (${attempt}/${retries})…`);
    }

    try {
      const res = await fetch(url, { ...options, signal });
      stopTicker();
      if (RETRYABLE.has(res.status) && attempt < retries) {
        lastRes = res;
        onStatus?.(`NVIDIA service busy… retrying (${attempt + 1}/${retries})`);
        await sleep(RETRY_DELAYS_MS[attempt] ?? 3500);
        continue;
      }
      return res;
    } catch (err) {
      stopTicker();
      if (signal?.aborted || err?.name === "AbortError") {
        throw new Error(
          timeoutMs
            ? "Request timed out. NVIDIA may still be processing — try again in a moment."
            : "Request cancelled."
        );
      }
      if (attempt < retries) {
        onStatus?.(`Connection issue… retrying (${attempt + 1}/${retries})`);
        await sleep(RETRY_DELAYS_MS[attempt] ?? 3500);
        continue;
      }
      throw friendlyNetworkError(err);
    }
  }
  return lastRes;
}

async function handle(res) {
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    if (RETRYABLE.has(res.status)) {
      msg =
        "NVIDIA NIM is temporarily unavailable. Wait a moment and try again.";
    }
    try {
      const data = await res.json();
      if (typeof data.error === "string") {
        msg = data.error;
      } else if (typeof data.errorMessage === "string") {
        msg = data.errorMessage;
      } else if (data.message && typeof data.message === "string") {
        msg = data.message;
      } else if (Array.isArray(data.error)) {
        msg = data.error.map((d) => d?.msg || d?.message || String(d)).join("; ");
      } else if (data.message) {
        msg = data.message;
      }
    } catch {
      /* ignore */
    }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

async function readSseStream(res, { onDelta, onStatus, signal } = {}) {
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let full = "";
  let gotDelta = false;

  while (true) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buffer.indexOf("\n")) !== -1) {
      const line = buffer.slice(0, idx).trim();
      buffer = buffer.slice(idx + 1);
      if (!line || !line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") return full;
      try {
        const json = JSON.parse(payload);
        const delta = json?.choices?.[0]?.delta?.content || "";
        if (delta) {
          if (!gotDelta) {
            gotDelta = true;
            onStatus?.("Receiving response…");
          }
          full += delta;
          onDelta?.(delta, full);
        }
      } catch {
        /* ignore parse errors on partial lines */
      }
    }
  }
  return full;
}

async function streamRequest(
  url,
  body,
  { onDelta, onStatus, signal, timeoutMs, processingMessage } = {}
) {
  const res = await fetchWithRetry(
    url,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    },
    {
      onStatus,
      timeoutMs: timeoutMs ?? 180000,
      processingMessage: processingMessage ?? "Waiting for NVIDIA response…",
    }
  );

  if (!res.ok || !res.body) {
    let msg = `Stream failed (${res.status})`;
    if (RETRYABLE.has(res.status)) {
      msg =
        "NVIDIA NIM is temporarily unavailable. Wait a moment and try again.";
    }
    try {
      const data = await res.json();
      msg = data.message || data.error || msg;
    } catch {}
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  onStatus?.("Waiting for first response…");
  return readSseStream(res, { onDelta, onStatus, signal });
}

export async function getModels() {
  return handle(
    await fetchWithRetry(apiUrl("/api/models"), {}, { retries: 1, timeoutMs: 30000 })
  );
}

export async function getHealth() {
  return handle(
    await fetchWithRetry(apiUrl("/api/health"), {}, { retries: 1, timeoutMs: 30000 })
  );
}

/** Ping the API function so the first real request is faster. */
export function warmApi() {
  fetchWithRetry(apiUrl("/api/health"), {}, { retries: 0, timeoutMs: 30000 }).catch(
    () => {}
  );
}

function createJobId() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `job-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

async function runNimJob(tool, payload, { onStatus, signal, timeoutMs = 15 * 60 * 1000 } = {}) {
  const jobId = createJobId();
  onStatus?.("Queued on NVIDIA NIM...");

  const start = await fetchWithRetry(
    apiUrl("/api/jobs/run"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jobId, tool, payload }),
      signal,
    },
    {
      retries: 1,
      timeoutMs: 30000,
      connectingMessage: "Starting background job...",
      processingMessage: "Starting background job...",
    }
  );

  if (!start.ok && start.status !== 202) {
    await handle(start);
  }

  const startedAt = Date.now();
  let polls = 0;

  while (Date.now() - startedAt < timeoutMs) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    await sleep(polls < 3 ? 1500 : 3000);
    polls += 1;

    const res = await fetchWithRetry(
      apiUrl(`/api/jobs/${encodeURIComponent(jobId)}`),
      { signal },
      {
        retries: 1,
        timeoutMs: 30000,
        connectingMessage: "Checking job status...",
        processingMessage: "Checking job status...",
      }
    );

    const job = await res.json();
    if (job.status === "queued" || job.status === "running") {
      onStatus?.(job.message || "NVIDIA NIM is processing your request...");
      continue;
    }

    if (job.status === "completed") {
      onStatus?.("Done.");
      return job.result;
    }

    if (job.status === "failed") {
      const err = new Error(job.error || "NVIDIA job failed.");
      err.status = job.errorStatus || 500;
      err.payload = job.payload;
      throw err;
    }
  }

  throw new Error("NVIDIA job timed out after 15 minutes.");
}

function extractAssistantText(data) {
  return data?.choices?.[0]?.message?.content || data?.choices?.[0]?.text || "";
}

export async function chatComplete({ model, messages, temperature, max_tokens }) {
  return handle(
    await fetchWithRetry(apiUrl("/api/chat"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model, messages, temperature, max_tokens }),
    })
  );
}

export async function chatStream(
  { model, messages, temperature, max_tokens },
  { onDelta, onStatus, signal } = {}
) {
  if (USE_ASYNC_JOBS) {
    const data = await runNimJob(
      "chat",
      { model, messages, temperature, max_tokens, stream: false },
      { onStatus, signal }
    );
    const text = extractAssistantText(data);
    if (text) onDelta?.(text, text);
    return text;
  }

  return streamRequest(
    apiUrl("/api/chat"),
    { model, messages, temperature, max_tokens, stream: true },
    { onDelta, onStatus, signal, processingMessage: "NVIDIA is thinking…" }
  );
}

export async function generateImage(opts, { onStatus, signal } = {}) {
  if (USE_ASYNC_JOBS) {
    return runNimJob("image", opts, {
      onStatus,
      signal,
      timeoutMs: 15 * 60 * 1000,
    });
  }

  return handle(
    await fetchWithRetry(
      apiUrl("/api/image"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts),
        signal,
      },
      {
        onStatus,
        timeoutMs: 180000,
        processingMessage: "Generating your image…",
      }
    )
  );
}

export async function generateVideo(opts, { onProgress, onStatus, signal } = {}) {
  if (USE_ASYNC_JOBS) {
    return runNimJob("video", opts, {
      onStatus: (message) => {
        onStatus?.(message);
        onProgress?.(message);
      },
      signal,
      timeoutMs: 15 * 60 * 1000,
    });
  }

  const start = await handle(
    await fetchWithRetry(
      apiUrl("/api/video"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts),
        signal,
      },
      {
        onStatus,
        timeoutMs: 120000,
        processingMessage: "Starting video on NVIDIA NIM…",
      }
    )
  );

  if (!start.pending) {
    onStatus?.("Video ready.");
    return start;
  }

  const { reqId } = start;
  const deadline = Date.now() + 3 * 60 * 1000;
  let polls = 0;

  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    polls += 1;
    const msg = "NVIDIA is rendering your video (30s–3min)…";
    onProgress?.(msg);
    onStatus?.(`${msg} check ${polls}`);

    await new Promise((r) => setTimeout(r, 4000));
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const poll = await fetchWithRetry(
      apiUrl(`/api/video/status/${encodeURIComponent(reqId)}`),
      { signal },
      { retries: 1, timeoutMs: 60000 }
    );
    const data = await handle(poll);
    if (!data.pending) {
      onStatus?.("Video ready.");
      return data;
    }
  }

  throw new Error("Video generation timed out after 3 minutes.");
}

export async function generateFullVideoFromPrompt(
  opts,
  { onStatus, onProgress, signal, timeoutMs = 20 * 60 * 1000 } = {}
) {
  onStatus?.("Starting full video generation...");
  const start = await handle(
    await fetchWithRetry(
      apiUrl("/api/video/full"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts || {}),
        signal,
      },
      {
        retries: 1,
        timeoutMs: 30000,
        processingMessage: "Starting full video generation...",
      }
    )
  );

  if (!start?.pending || !start?.jobId) {
    throw new Error("Failed to start full video generation job.");
  }

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    await sleep(3000);
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const poll = await fetchWithRetry(
      apiUrl(`/api/video/full/status/${encodeURIComponent(start.jobId)}`),
      { signal },
      {
        retries: 1,
        timeoutMs: 30000,
        processingMessage: "Checking full video status...",
      }
    );
    const job = await poll.json();

    if (job.status === "queued" || job.status === "running") {
      const msg = job.message || "Generating full video...";
      onStatus?.(msg);
      onProgress?.(msg);
      continue;
    }

    if (job.status === "completed") {
      onStatus?.("Full video ready.");
      return normalizeShortsResult(job.result);
    }

    if (job.status === "failed") {
      const err = new Error(job.error || "Full video generation failed.");
      err.status = job.errorStatus || 500;
      err.payload = job.payload;
      throw err;
    }
  }

  throw new Error("Full video generation timed out.");
}

export async function generateShortFromYoutube(
  opts,
  { onStatus, onProgress, signal, timeoutMs = 20 * 60 * 1000 } = {}
) {
  onStatus?.("Starting Shorts pipeline...");
  const start = await handle(
    await fetchWithRetry(
      apiUrl("/api/shorts"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts || {}),
        signal,
      },
      {
        retries: 1,
        timeoutMs: 30000,
        processingMessage: "Starting Shorts pipeline...",
      }
    )
  );

  if (!start?.pending || !start?.jobId) {
    throw new Error("Failed to start Shorts generation job.");
  }

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    await sleep(3000);
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const poll = await fetchWithRetry(
      apiUrl(`/api/shorts/status/${encodeURIComponent(start.jobId)}`),
      { signal },
      { retries: 1, timeoutMs: 30000, processingMessage: "Checking Shorts status..." }
    );
    const job = await poll.json();

    if (job.status === "queued" || job.status === "running") {
      const msg = job.message || "Generating short video...";
      onStatus?.(msg);
      onProgress?.(msg);
      continue;
    }

    if (job.status === "completed") {
      onStatus?.("Short video ready.");
      return normalizeShortsResult(job.result);
    }

    if (job.status === "failed") {
      const err = new Error(job.error || "Shorts generation failed.");
      err.status = job.errorStatus || 500;
      err.payload = job.payload;
      throw err;
    }
  }

  throw new Error("Shorts generation timed out.");
}

function normalizeModel3dResult(result) {
  if (!result || typeof result !== "object") return result;
  return {
    ...result,
    modelUrl: normalizeApiResourceUrl(result.modelUrl || result.downloadUrl),
    downloadUrl: normalizeApiResourceUrl(result.downloadUrl || result.modelUrl),
    stlDownloadUrl: normalizeApiResourceUrl(result.stlDownloadUrl),
    referenceImageUrl: normalizeApiResourceUrl(result.referenceImageUrl),
  };
}

async function runModel3dJob(
  path,
  opts,
  { onStatus, onProgress, signal, timeoutMs = 20 * 60 * 1000 } = {}
) {
  onStatus?.("Starting 3D model generation...");
  const start = await handle(
    await fetchWithRetry(
      apiUrl(path),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts || {}),
        signal,
      },
      {
        retries: 1,
        timeoutMs: 30000,
        processingMessage: "Starting 3D model generation...",
      }
    )
  );

  if (!start?.pending || !start?.jobId) {
    throw new Error("Failed to start 3D model generation job.");
  }

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    await sleep(3000);
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const poll = await fetchWithRetry(
      apiUrl(`/api/model3d/status/${encodeURIComponent(start.jobId)}`),
      { signal },
      {
        retries: 1,
        timeoutMs: 30000,
        processingMessage: "Checking 3D model status...",
      }
    );
    const job = await poll.json();

    if (job.status === "queued" || job.status === "running") {
      const msg = job.message || "Generating 3D model...";
      onStatus?.(msg);
      onProgress?.(msg);
      continue;
    }

    if (job.status === "completed") {
      onStatus?.("3D model ready.");
      return normalizeModel3dResult(job.result);
    }

    if (job.status === "failed") {
      const err = new Error(job.error || "3D model generation failed.");
      err.status = job.errorStatus || 500;
      err.payload = job.payload;
      throw err;
    }
  }

  throw new Error("3D model generation timed out.");
}

export async function generateModel3d(
  opts,
  config = {}
) {
  return runModel3dJob("/api/model3d", opts, config);
}

export async function executeCadQueryCode(code, config = {}) {
  return runModel3dJob("/api/model3d/execute-cad", { code }, config);
}

export async function refineGameModel(opts, config = {}) {
  return runModel3dJob("/api/model3d/refine-game", opts, config);
}

export async function ttsServer(opts, { onStatus, signal } = {}) {
  if (USE_ASYNC_JOBS) {
    return runNimJob("tts", opts, {
      onStatus,
      signal,
      timeoutMs: 15 * 60 * 1000,
    });
  }

  return handle(
    await fetchWithRetry(
      apiUrl("/api/audio/tts"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(opts),
        signal,
      },
      {
        onStatus,
        timeoutMs: 120000,
        processingMessage: "Generating speech on NVIDIA NIM…",
      }
    )
  );
}

export async function transcribeServer({ model, audio, language } = {}, config = {}) {
  if (USE_ASYNC_JOBS) {
    return runNimJob(
      "transcribe",
      { model, audio, language },
      {
        onStatus: config.onStatus,
        signal: config.signal,
        timeoutMs: 15 * 60 * 1000,
      }
    );
  }

  return handle(
    await fetchWithRetry(
      apiUrl("/api/audio/transcribe"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model, audio, language }),
        signal: config.signal,
      },
      {
        onStatus: config.onStatus,
        timeoutMs: 120000,
        processingMessage: "Transcribing on NVIDIA NIM…",
      }
    )
  );
}

export async function codeAssist({
  model,
  prompt,
  code,
  language,
  mode,
  projectMode,
  max_tokens = 2048,
} = {}) {
  return handle(
    await fetchWithRetry(apiUrl("/api/code"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model,
        prompt,
        code,
        language,
        mode,
        projectMode,
        max_tokens,
      }),
    })
  );
}

export async function codeStream(
  { model, prompt, code, language, mode, projectMode, max_tokens = 2048 },
  { onDelta, onStatus, signal } = {}
) {
  if (USE_ASYNC_JOBS) {
    const data = await runNimJob(
      "code",
      {
        model,
        prompt,
        code,
        language,
        mode,
        projectMode,
        max_tokens,
        stream: false,
      },
      { onStatus, signal }
    );
    const text = extractAssistantText(data);
    if (text) onDelta?.(text, text);
    return text;
  }

  return streamRequest(
    apiUrl("/api/code"),
    {
      model,
      prompt,
      code,
      language,
      mode,
      projectMode,
      max_tokens,
      stream: true,
    },
    { onDelta, onStatus, signal, processingMessage: "NVIDIA is writing code…" }
  );
}
